import type {QueryDocumentSnapshot} from 'firebase/firestore'
import {collection, doc, type DocumentReference} from 'firebase/firestore'
import type {Course, CourseChat, CourseStudentStatus} from '@goschool/model'
import {getActiveCourseAccessDisabledWindow, resolveCourseStudentStatus, useCourseStudent, useUserCourseChats} from '@goschool/model'
import {useUserContext} from '@goschool/auth'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {typeConverter} from '@goschool/react-firebase'
import {useChatManager} from './useChatManager'

export function useChats(
  courseSnapshot: QueryDocumentSnapshot<Course>,
  courseChatSnapshot?: QueryDocumentSnapshot<CourseChat>,
  handleChatSelect?: () => void) {
  const {roles, user} = useUserContext()

  const collectionRef = useMemo(
    () =>
      collection(courseSnapshot.ref, 'chats').withConverter(typeConverter<CourseChat>()),
    [courseSnapshot]
  )


  const chats = useUserCourseChats(courseSnapshot, user)
  const studentSnapshot = useCourseStudent(courseSnapshot, user)

  const studentStatus: CourseStudentStatus | undefined = useMemo(
    () => {
      const data = studentSnapshot?.data()
      if (data == null) {
        return undefined
      }
      return resolveCourseStudentStatus(data)
    },
    [studentSnapshot]
  )

  const activeDisabledWindow = useMemo(
    () => getActiveCourseAccessDisabledWindow(courseSnapshot.data(), 'chat'),
    [courseSnapshot]
  )
  const isCourseStaff = useMemo(
    () => {
      const course = courseSnapshot.data()
      return course.owner === user?.uid ||
        roles.includes('admin') ||
        course.instructors.some(instructor => instructor.ref.id === user?.uid)
    },
    [courseSnapshot, roles, user]
  )

  const canCreateChat = (studentStatus == null ? true : studentStatus === 'active') &&
    (activeDisabledWindow == null || isCourseStaff)

  const [selectedChat, setSelectedChat] = useState<DocumentReference<CourseChat>>()

  useEffect(() => {
    if (courseChatSnapshot != null) {
      setSelectedChat(courseChatSnapshot.ref)
    }
  }, [courseChatSnapshot])

  const selectChat = useCallback(
    (chat: DocumentReference<CourseChat>) => {
      setSelectedChat(chat)
      handleChatSelect?.()
    },
    [setSelectedChat, handleChatSelect]
  )

  const createChat = useCallback(
    () => {
      if (!canCreateChat) {
        return
      }
      selectChat(doc(collectionRef))
    },
    [collectionRef, selectChat, canCreateChat]
  )

  useEffect(
    () => {
      if (chats != null) {
        if (selectedChat == null) {
          if (chats.length > 0) {
            selectChat(chats[0].ref)
          } else if (canCreateChat) {
            createChat()
          }
        }
      }
    },
    [selectedChat, chats, createChat, selectChat, canCreateChat]
  )

  const chatManager = useChatManager(user, selectedChat)


  return {
    collectionRef,
    chats,
    selectedChat,
    selectChat,
    createChat,
    chatManager,
    studentStatus,
    canCreateChat
  }
}
